import React, { useState } from 'react'
import {
	Modal,
	Button,
	TextField,
	FormControlLabel,
	Switch,
	Box,
	Typography,
	IconButton,
	InputAdornment,
} from '@mui/material'
import { useAppDispatch } from '@redux/index'
import { seasonActions } from '@redux/store/index'
import CloseIcon from '@mui/icons-material/Close'
import { css } from '@emotion/react'
import { SeasonCreationType } from '@schemas'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AttachMoney, CalendarToday } from '@mui/icons-material'
import dayjs from 'dayjs'

interface SeasonRegistrationModalProps {
	open: boolean
	onClose: () => void
}

export const SeasonRegistrationModal: React.FC<SeasonRegistrationModalProps> = ({
	open,
	onClose,
}) => {
	const dispatch = useAppDispatch()
	const [seasonData, setSeasonData] = useState<SeasonCreationType>({
		title: '',
		startDate: '',
		endDate: '',
		activeYn: 0,
		targetUsd: 0,
		targetQuantity: 0,
	})
	const [errors, setErrors] = useState<{ [key: string]: string }>({})

	const validateForm = () => {
		const newErrors: { [key: string]: string } = {}
		if (!seasonData.title.trim()) newErrors.title = '시즌 제목을 입력해주세요.'
		if (!seasonData.startDate) newErrors.startDate = '시작 날짜를 선택해주세요.'
		if (!seasonData.endDate) newErrors.endDate = '종료 날짜를 선택해주세요.'
		if (seasonData.targetUsd <= 0)
			newErrors.targetUsd = '유효한 목표 금액을 입력해주세요.'
		if (seasonData.targetQuantity <= 0)
			newErrors.targetQuantity = '유효한 목표 수량을 입력해주세요.'
		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleInputChange = (
		field: keyof SeasonCreationType,
		value: string | number | boolean,
	) => {
		setSeasonData(prev => ({ ...prev, [field]: value }))
	}

	const handleRegister = () => {
		if (validateForm()) {
			const _seasonData = {
				...seasonData,
				startDate: dayjs(seasonData.startDate).format('YYYY-MM-DD'),
				endDate: dayjs(seasonData.endDate).format('YYYY-MM-DD'),
			}
			dispatch(seasonActions.REGISTER_SEASON(_seasonData))
			handleClose()
		}
	}

	const handleClose = () => {
		setSeasonData({
			title: '',
			startDate: '',
			endDate: '',
			activeYn: 0,
			targetUsd: 0,
			targetQuantity: 0,
		})
		setErrors({})
		onClose()
	}

	return (
		<Modal open={open} onClose={onClose}>
			<Box sx={styles.modalContent}>
				<IconButton onClick={handleClose} sx={styles.closeButton} aria-label="close">
					<CloseIcon />
				</IconButton>
				<Typography variant="h5" component="h2" sx={styles.title}>
					시즌 등록
				</Typography>
				<TextField
					label="시즌 제목"
					value={seasonData.title}
					onChange={e => handleInputChange('title', e.target.value)}
					fullWidth
					margin="normal"
					error={!!errors.title}
					helperText={errors.title}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<CalendarToday />
							</InputAdornment>
						),
					}}
					sx={{ mb: 3 }}
				/>
				<LocalizationProvider dateAdapter={AdapterDateFns}>
					<Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
						<Box sx={{ flex: 1 }}>
							<DatePicker
								label="시작 날짜"
								value={seasonData.startDate ? new Date(seasonData.startDate) : null}
								onChange={date =>
									handleInputChange('startDate', date ? date.toISOString() : '')
								}
								format="yyyy-MM-dd"
							/>
						</Box>
						<Box sx={{ flex: 1 }}>
							<DatePicker
								label="종료 날짜"
								value={seasonData.endDate ? new Date(seasonData.endDate) : null}
								onChange={date =>
									handleInputChange('endDate', date ? date.toISOString() : '')
								}
								format="yyyy-MM-dd"
							/>
						</Box>
					</Box>
				</LocalizationProvider>
				<TextField
					label="목표 금액 (USD)"
					value={seasonData.targetUsd}
					onChange={e => handleInputChange('targetUsd', Number(e.target.value))}
					fullWidth
					margin="normal"
					type="number"
					error={!!errors.targetUsd}
					helperText={errors.targetUsd}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<AttachMoney />
							</InputAdornment>
						),
					}}
					sx={{ mb: 1 }}
				/>
				<TextField
					label="목표 수량"
					value={seasonData.targetQuantity}
					onChange={e => handleInputChange('targetQuantity', Number(e.target.value))}
					fullWidth
					margin="normal"
					type="number"
					error={!!errors.targetQuantity}
					helperText={errors.targetQuantity}
					sx={{ mb: 1 }}
				/>
				<FormControlLabel
					control={
						<Switch
							checked={seasonData.activeYn === 1}
							onChange={e => handleInputChange('activeYn', e.target.checked ? 1 : 0)}
							color="primary"
						/>
					}
					label="활성화"
					sx={{ ...styles.switch, mt: 3 }}
				/>
				<Button
					onClick={handleRegister}
					variant="contained"
					color="primary"
					fullWidth
					sx={styles.button}
				>
					등록
				</Button>
			</Box>
		</Modal>
	)
}

const styles = {
	modalContent: css`
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: 400px;
		background-color: white;
		box-shadow: 0px 11px 15px -7px rgba(0, 0, 0, 0.2),
			0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12);
		padding: 32px;
		border-radius: 8px;
	`,
	closeButton: css`
		position: absolute;
		right: 8px;
		top: 8px;
	`,
	title: css`
		margin-bottom: 16px;
	`,
	datePickerContainer: css`
		margin-top: 16px;
		margin-bottom: 16px;
	`,
	switch: {
		marginBottom: '16px',
	},
	button: {
		marginTop: '16px',
	},
}

export default SeasonRegistrationModal
